import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send, Paperclip, X, FileText, Image as ImageIcon } from "lucide-react";
import { trackFormConversion, trackPhoneConversion } from "@/lib/gtagConversion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FORMATIONS = ["Permis B", "Moto A2", "Permis AM", "Poids Lourd C", "E-Learning", "Autre"];
const ZONES = ["Boulogne-Billancourt", "Porte de Saint-Cloud", "Porte d'Auteuil", "Place d'Italie", "Sans préférence"];

const ACCEPTED_MIMES = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
const ACCEPTED_EXTS = [".pdf", ".jpg", ".jpeg", ".png"];
const MAX_FILES = 5;
const MAX_SIZE = 10 * 1024 * 1024; // 10 MB

function humanSize(bytes) {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", formation: "", zone: "", message: "" });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileInput = useRef(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addFiles = (incoming) => {
    const next = [...files];
    for (const f of Array.from(incoming)) {
      const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
      const okType = ACCEPTED_MIMES.includes(f.type) || ACCEPTED_EXTS.includes(ext);
      if (!okType) { toast.error(`${f.name} : format non autorisé (PDF, JPG, PNG uniquement)`); continue; }
      if (f.size > MAX_SIZE) { toast.error(`${f.name} : trop volumineux (10 Mo max)`); continue; }
      if (next.length >= MAX_FILES) { toast.error(`Maximum ${MAX_FILES} fichiers`); break; }
      if (next.some((x) => x.name === f.name && x.size === f.size)) continue; // dedupe
      next.push(f);
    }
    setFiles(next);
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      files.forEach((f) => fd.append("files", f, f.name));
      await axios.post(`${API}/contact`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      trackFormConversion({ email: form.email, phone: form.phone });
      toast.success("Merci ! Nous vous recontactons très rapidement.");
      setForm({ name: "", email: "", phone: "", formation: "", zone: "", message: "" });
      setFiles([]);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur lors de l'envoi — réessayez ou WhatsApp.";
      toast.error(typeof msg === "string" ? msg : "Erreur lors de l'envoi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="anchor-offset bg-[#f8faff] text-[#1a2332]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[#FF8C42] font-headings font-bold text-sm uppercase tracking-[0.2em]">Contact</div>
            <h2 className="mt-3 font-headings font-black text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Prêt à prendre la route ?
            </h2>
            <p className="mt-4 text-[#5b6577] text-lg max-w-lg">
              Envoyez-nous un message, nous revenons vers vous très rapidement. Ou passez nous voir directement, le café est offert.
            </p>

            <div className="mt-10 space-y-5">
              <InfoRow icon={MapPin} title="Adresse" text="37 Av. Édouard Vaillant, 92100 Boulogne-Billancourt" />
              <InfoRow icon={Phone} title="Téléphone" text="01 41 31 50 61 · WhatsApp +33 6 72 64 63 18" />
              <InfoRow icon={Mail} title="Email" text="dynastieblb@gmail.com" />
              <InfoRow icon={Clock} title="Horaires conduite" text="Lun. au Sam. 7h — 22h · Dimanche 10h — 20h" />
              <InfoRow icon={Clock} title="Horaires bureau" text="Lun. au Ven. 10h — 21h · Samedi 10h — 18h30" />
            </div>

            <div className="mt-8 rounded-2xl overflow-hidden border border-black/5 shadow-sm aspect-video bg-white">
              <iframe
                title="DDSBillancourt Map"
                width="100%" height="100%"
                src="https://www.openstreetmap.org/export/embed.html?bbox=2.22%2C48.82%2C2.27%2C48.85&layer=mapnik&marker=48.8351%2C2.2407"
                style={{ border: 0 }}
                loading="lazy"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-white border border-black/5 rounded-3xl p-6 md:p-10 shadow-sm"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Prénom & Nom" required>
                  <input data-testid="contact-name" required value={form.name} onChange={update("name")} className="input-dds" placeholder="Marie Dupont" />
                </Field>
                <Field label="Email" required>
                  <input data-testid="contact-email" type="email" required value={form.email} onChange={update("email")} className="input-dds" placeholder="marie@exemple.fr" />
                </Field>
                <Field label="Téléphone">
                  <input data-testid="contact-phone" value={form.phone} onChange={update("phone")} className="input-dds" placeholder="06 12 34 56 78" />
                </Field>
                <Field label="Formation souhaitée">
                  <select data-testid="contact-formation" value={form.formation} onChange={update("formation")} className="input-dds">
                    <option value="">Choisir…</option>
                    {FORMATIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </Field>
                <Field label="Zone de conduite souhaitée">
                  <select data-testid="contact-zone" value={form.zone} onChange={update("zone")} className="input-dds">
                    <option value="">Choisir…</option>
                    {ZONES.map((z) => <option key={z} value={z}>{z}</option>)}
                  </select>
                </Field>
              </div>
              <div className="mt-5">
                <Field label="Message">
                  <textarea data-testid="contact-message" value={form.message} onChange={update("message")} rows={5} className="input-dds resize-none" placeholder="Dites-nous ce dont vous avez besoin (optionnel)…" />
                </Field>
              </div>

              <div className="mt-5" data-testid="contact-attachments">
                <span className="font-headings font-semibold text-sm text-[#1a2332]">
                  Pièces jointes <span className="text-[#5b6577] font-normal">(optionnel · PDF, JPG, PNG · max 5 fichiers, 10 Mo chacun)</span>
                </span>
                <div className="mt-2 space-y-3">
                  <div
                    onDragOver={(e) => { e.preventDefault(); }}
                    onDrop={(e) => { e.preventDefault(); if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files); }}
                    onClick={() => fileInput.current?.click()}
                    className="cursor-pointer border-2 border-dashed border-[#00a86b]/40 hover:border-[#00a86b] bg-[#f8faff] rounded-xl px-5 py-6 flex items-center gap-3 transition-colors"
                    data-testid="contact-attachments-dropzone"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileInput.current?.click(); }}
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#00a86b]/10 flex items-center justify-center shrink-0">
                      <Paperclip className="w-5 h-5 text-[#00a86b]" />
                    </div>
                    <div className="flex-1">
                      <div className="font-headings font-bold text-[#1a2332] text-sm">
                        Glissez vos fichiers ici ou cliquez pour parcourir
                      </div>
                      <div className="text-xs text-[#5b6577] mt-0.5">
                        Idéal pour joindre pièce d&apos;identité, justificatif de domicile, ANTS, ASSR…
                      </div>
                    </div>
                    <span className="text-xs font-headings font-bold text-[#00a86b]">
                      {files.length}/{MAX_FILES}
                    </span>
                  </div>
                  <input
                    ref={fileInput}
                    type="file"
                    data-testid="contact-attachments-input"
                    className="hidden"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                    onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ""; }}
                  />
                  {files.length > 0 && (
                    <ul className="space-y-2" data-testid="contact-attachments-list">
                      {files.map((f, i) => {
                        const isPdf = (f.type === "application/pdf") || f.name.toLowerCase().endsWith(".pdf");
                        return (
                          <li key={i} className="flex items-center gap-3 bg-white border border-black/5 rounded-xl px-4 py-2.5 shadow-sm" data-testid={`contact-attachment-${i}`}>
                            {isPdf ? <FileText className="w-5 h-5 text-[#FF8C42] shrink-0" /> : <ImageIcon className="w-5 h-5 text-[#00a86b] shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <div className="font-headings font-semibold text-sm text-[#1a2332] truncate">{f.name}</div>
                              <div className="text-xs text-[#5b6577]">{humanSize(f.size)}</div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(i)}
                              data-testid={`contact-attachment-remove-${i}`}
                              className="w-8 h-8 rounded-lg text-[#5b6577] hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors"
                              aria-label={`Retirer ${f.name}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>

              <button
                type="submit"
                data-testid="contact-form-submit"
                disabled={loading}
                className="mt-6 w-full md:w-auto inline-flex items-center justify-center gap-2 bg-[#FF8C42] hover:bg-[#e8763a] disabled:opacity-60 text-white font-headings font-bold rounded-xl px-7 py-4 shadow-lg hover:-translate-y-0.5 transition-all"
              >
                {loading ? "Envoi…" : <>Envoyer <Send className="w-4 h-4" /></>}
              </button>
              <p className="mt-4 text-xs text-[#5b6577]">En soumettant ce formulaire vous acceptez d&apos;être recontacté par DDSBillancourt.</p>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .input-dds {
          width: 100%;
          background: #f8faff;
          border: 2px solid #e5eaf3;
          color: #1a2332;
          padding: 0.85rem 1rem;
          border-radius: 0.85rem;
          font-family: 'Inter', sans-serif;
          outline: none;
          transition: all 0.2s;
        }
        .input-dds::placeholder { color: #9aa5bc; }
        .input-dds:focus { border-color: #00a86b; background: #fff; }
      `}</style>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-headings font-semibold text-sm text-[#1a2332]">
        {label} {required && <span className="text-[#FF8C42]">*</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function InfoRow({ icon: Icon, title, text }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-xl bg-white border border-black/5 shadow-sm flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-[#00a86b]" />
      </div>
      <div>
        <div className="font-headings font-bold text-[#1a2332]">{title}</div>
        <div className="text-[#5b6577] text-sm">{text}</div>
      </div>
    </div>
  );
}
